import React from 'react';
import { HSSEComplianceBriefing } from '@types';
import { md, Section, Disclaimer, ICONS } from './ReportShared';
import { printRenderedReport } from '../../utils/printRenderedReport';

// --- HSSE Briefing Action Bar ---
export const HsseReportActionBar: React.FC<{
    report: HSSEComplianceBriefing;
    onSave: () => void;
    isSaving: boolean;
    readOnly?: boolean;
}> = ({ report, onSave, isSaving, readOnly = false }) => {

    const handleDownloadReport = async () => {
        printRenderedReport("HSSE Compliance Report");
    };

    const handleDownloadTemplate = () => {
        const blob = new Blob([report.modelProgram], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'HSSE_Compliance_Plan_Template.md';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-6 sticky top-0 z-10 shadow-sm">
            <h3 className="text-lg font-bold text-slate-800 mb-2">Executive Summary</h3>
            <p className="text-sm text-slate-600 mb-4">{report.summary || (report as any).executiveSummary}</p>
            {!readOnly && <div className="flex flex-wrap gap-2">
                <button onClick={onSave} disabled={isSaving} className="bg-green-600 hover:bg-green-700 disabled:bg-slate-400 text-white font-semibold py-2 px-3 rounded-md flex items-center transition-colors text-xs">
                    {React.isValidElement(ICONS.save) ? React.cloneElement(ICONS.save as any, {className: 'h-4 w-4 mr-2'}) : null}
                    {isSaving ? 'Saving...' : 'Save to Library'}
                </button>
                <button onClick={handleDownloadReport} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-3 rounded-md flex items-center transition-colors text-xs">
                     {React.isValidElement(ICONS.download) ? React.cloneElement(ICONS.download as any, {className: 'h-4 w-4 mr-2'}) : null}
                    Print / Save PDF
                </button>
                 <button onClick={handleDownloadTemplate} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-3 rounded-md flex items-center transition-colors text-xs">
                     {React.isValidElement(ICONS.document_text) ? React.cloneElement(ICONS.document_text as any, {className: 'h-4 w-4 mr-2'}) : null}
                    Download Template
                </button>
            </div>}
        </div>
    );
};

// --- HSSE Briefing Display Component ---
export const HsseBriefingDisplay: React.FC<{
    result: HSSEComplianceBriefing;
    onSave: () => void;
    isSaving: boolean;
    readOnly?: boolean;
}> = ({ result, onSave, isSaving, readOnly = false }) => {
    return (
        <div>
            <HsseReportActionBar
                report={result}
                onSave={onSave}
                isSaving={isSaving}
                readOnly={readOnly}
            />

            <div className="space-y-8">
                <Section title="Applicable Regulatory Bodies">
                    <ul className="list-disc list-inside space-y-2 text-sm text-slate-700">
                        {(result.regulatoryBodies || []).map((body, index) => (
                            <li key={index}><b>{body.name}:</b> {body.description}</li>
                        ))}
                    </ul>
                </Section>

                <Section title="Key Regulations">
                    <div className="space-y-4">
                        {(result.keyRegulations || []).map((req, index) => (
                             <div key={index} className="p-4 bg-white border border-slate-200 rounded-lg">
                                 <h5 className="font-bold text-slate-800">{req.regulation}</h5>
                                 <p className="text-sm text-slate-600 mt-1">{req.summary}</p>
                             </div>
                        ))}
                    </div>
                </Section>

                <Section title="Compliance Checklist">
                    <div className="space-y-3">
                         {(result.complianceChecklist || []).map((step, index) => (
                              <div key={index} className="flex items-start gap-4 p-3 bg-slate-50 border-l-4 border-blue-500">
                                 <div className="text-xl font-bold text-blue-600">0{index + 1}</div>
                                 <div>
                                     <h5 className="font-semibold text-slate-800">{step.item}</h5>
                                     <p className="text-sm text-slate-600">{step.description}</p>
                                 </div>
                             </div>
                         ))}
                    </div>
                </Section>

                <Section title="Model Compliance Program (Template)">
                     <div className="prose prose-sm max-w-none p-4 bg-white border border-dashed border-slate-300 rounded-md">
                         <div dangerouslySetInnerHTML={{ __html: md.render(result.modelProgram) }} />
                     </div>
                </Section>

                {/* Additional Intelligence Sections */}
                {(result as any).additionalSections && (result as any).additionalSections.length > 0 && (
                    <div className="space-y-8">
                        {(result as any).additionalSections.map((section: any, idx: number) => (
                            <Section key={idx} title={section.title}>
                                <div className="prose prose-sm text-slate-700 max-w-none" dangerouslySetInnerHTML={{ __html: md.render(section.content) }} />
                            </Section>
                        ))}
                    </div>
                )}
                 <Disclaimer text={result.disclaimer} />
            </div>
        </div>
    );
};

export default HsseBriefingDisplay;
